import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { RESOURCES_JSON_URL, UPLOADS_BASE_URL } from '../config';
import { ImageWithFallback } from './figma/ImageWithFallback';
 

interface FeaturedItem {
  id: number;
  title: string;
  subtitle: string;
  image: string;
  color: string;
  resourceId?: number;
}

const featuredItems: FeaturedItem[] = [];

type LinkedResource = {
  id: number;
  title: string;
  author: string;
  image: string;
  type: 'website' | 'template';
  description: string;
  content: string;
  tags: string[];
  isFree?: boolean;
  updateDate?: string;
  link?: string;
};

export function FeaturedCarousel({ onPick }: { onPick?: (r: LinkedResource) => void } = {}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [items, setItems] = useState<FeaturedItem[]>(featuredItems);
  const [resourceMap, setResourceMap] = useState<Record<number, LinkedResource>>({});
 

  useEffect(() => {
    const isDev = !!(import.meta as any).env?.DEV;
    const fixUrl = (u: string) => {
      if (!u) return '';
      if (u.startsWith('/uploads/')) return isDev ? u : `${UPLOADS_BASE_URL}${u.replace('/uploads', '')}`;
      return u;
    };
    const load = () => {
      const url = isDev ? '/content.json' : RESOURCES_JSON_URL;
      fetch(url)
        .then((r) => r.json())
        .then((d) => {
          const arr = Array.isArray(d?.featured) ? d.featured : [];
          const resources = Array.isArray(d?.resources) ? d.resources : [];
          const rmap: Record<number, LinkedResource> = {};
          for (const r of resources) {
            const rid = Number(r?.id);
            if (!Number.isNaN(rid)) {
              rmap[rid] = {
                id: rid,
                title: r.title,
                author: r.author,
                image: fixUrl(r.image),
                type: r.type,
                description: r.description || '',
                content: r.content || '',
                tags: Array.isArray(r.tags) ? r.tags : [],
                isFree: !!r.isFree,
                updateDate: r.updateDate || '',
                link: r.link || ''
              };
            }
          }
          setResourceMap(rmap);
          if (arr.length) {
            const mapped = arr.map((it: any) => ({
              id: it.id,
              title: it.title,
              subtitle: it.subtitle || '',
              image: fixUrl(it.image) || (it.resourceId && rmap[it.resourceId]?.image) || '',
              color: it.color || '',
              resourceId: typeof it.resourceId === 'number' ? it.resourceId : undefined,
            }));
            setItems(mapped);
          } else {
            setItems([]);
          }
        })
        .catch(() => {});
    };
    load();
    const timer = isDev ? setInterval(load, 5000) : null;
    return () => { if (timer) clearInterval(timer); };
  }, []);

  const extendedItems = items.length > 0 ? [
    items[items.length - 1],
    ...items,
    items[0],
  ] : [];

  // 保持纯逻辑，不引入动态内边距参与滚动计算

  useEffect(() => {
    // 等 items 加载并计算出 pad 后再设置初始滚动位置
    if (!scrollContainerRef.current || items.length === 0) return;
    const cardWidth = 500; // 卡片宽度
    const gap = 24; // 间距
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = (cardWidth + gap) * 2; // 显示第二张卡片
      }
    }, 0);
  }, [items.length]);

  // 处理滚动结束后的循环逻辑
  const handleScrollEnd = () => {
    if (!scrollContainerRef.current) return;
    
    const cardWidth = 500;
    const gap = 24;
    const step = cardWidth + gap;
    const left = scrollContainerRef.current.scrollLeft;
    const approxExtendedIndex = left / step;

    if (currentIndex === -1 || approxExtendedIndex <= 0.1) {
      scrollContainerRef.current.scrollLeft = (cardWidth + gap) * items.length;
      setCurrentIndex(items.length - 1);
      setIsTransitioning(false);
    }
    else if (currentIndex === items.length || approxExtendedIndex >= items.length + 0.9) {
      scrollContainerRef.current.scrollLeft = (cardWidth + gap) * 1;
      setCurrentIndex(0);
      setIsTransitioning(false);
    } else {
      setIsTransitioning(false);
    }
  };

  // 监听滚动事件，使用 debounce 检测滚动结束
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        handleScrollEnd();
      }, 150); // 滚动停止150ms后执行
    };

    container.addEventListener('scroll', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [currentIndex]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current || isTransitioning) return;
    
    setIsTransitioning(true);
    const cardWidth = 500;
    const gap = 24;

    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    setCurrentIndex(newIndex);

    const targetScroll = (cardWidth + gap) * (newIndex + 1);
    
    scrollContainerRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="relative bg-white overflow-hidden py-8">
      {/* 左侧按钮 */}
      <button
        onClick={() => scroll('left')}
        disabled={isTransitioning}
        className="absolute left-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-lg disabled:opacity-50"
      >
        <ChevronLeft className="w-5 h-5 text-gray-700" />
      </button>

      {/* 右侧按钮 */}
      <button
        onClick={() => scroll('right')}
        disabled={isTransitioning}
        className="absolute right-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-lg disabled:opacity-50"
      >
        <ChevronRight className="w-5 h-5 text-gray-700" />
      </button>

      {/* 轮播容器 - 居中对齐，左右露出部分卡片 */}
      <div
        ref={scrollContainerRef}
        className="flex gap-6 overflow-x-hidden scroll-smooth px-[calc(50vw-250px-12px)]"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {extendedItems.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="flex-shrink-0 w-[500px] h-[280px] relative rounded-md overflow-hidden group cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
            onClick={() => {
              if (!onPick) return;
              const resId = items[(index - 1 + items.length) % items.length]?.resourceId;
              if (typeof resId === 'number' && resourceMap[resId]) {
                onPick(resourceMap[resId]);
              }
            }}
          >
            {/* 背景图片 */}
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />

            {/* 渐变遮罩 */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

            {/* 文字内容 */}
            <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
              <h2 className="text-xl font-bold mb-1.5 transform group-hover:translate-y-[-4px] transition-transform duration-300">
                {item.title}
              </h2>
              <p className="text-sm opacity-90 leading-relaxed">
                {item.subtitle}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
